import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { votingApi } from '../../services/api';
import { User, VotingSession, Vote } from '../../types';
import { averageVote } from '../../utils/voteStats';

interface SessionResultsProps {
  sessionId: string;
  participants: User[];
}

/** Fetches and shows the vote breakdown for one server-tracked session. */
const SessionResults: React.FC<SessionResultsProps> = ({ sessionId, participants }) => {
  const { t } = useTranslation();
  const [results, setResults] = useState<{ session: VotingSession; votes: Vote[] } | null>(null);
  const [loading, setLoading] = useState(true);

  const loadResults = useCallback(async () => {
    try {
      const data = await votingApi.getSessionResults(sessionId);
      setResults(data);
    } catch (err) {
      console.error('Failed to load session results:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  if (loading) {
    return <div className="results-loading">{t('room.status.connecting') || '結果を読み込み中...'}...</div>;
  }

  if (!results) {
    return <div className="results-error">{t('errors.genericError') || '結果を読み込めませんでした'}</div>;
  }

  const average = averageVote(results.votes);

  return (
    <div className="session-results">
      <div className="results-summary">
        <span className="vote-count">{t('room.results.participant') || '投票数'}: {results.votes.length}</span>
        <span className="vote-average">
          {t('room.results.average') || '平均'}: {average === undefined ? 'N/A' : average.toFixed(1)}
        </span>
      </div>

      {results.session.is_revealed && results.votes.length > 0 && (
        <div className="votes-list">
          {results.votes.map((vote) => {
            const voter = participants.find((p) => p.id === vote.user_id);
            return (
              <div key={vote.user_id} className="vote-item">
                <span className="voter-name">{voter?.display_name || `User ${vote.user_id}`}</span>
                <span className="vote-value">{vote.value}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionResults;
